import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  ArrowDownRight, ArrowUpRight, Check, ChevronRight, Code2, Database,
  Github, Linkedin, Mail, Menu, Server, ShieldCheck, Terminal, X, Zap
} from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import Admin from './components/Admin';
import SEO from './components/SEO';
import resumePdf from './Assets/Adrian_Rudge_Resume.pdf';

const capabilities = [
  { icon: Database, label: 'Data & SQL', detail: 'SQL Server, T-SQL, integrations' },
  { icon: Code2, label: 'Applications', detail: 'C#, .NET, React, Node.js' },
  { icon: Server, label: 'Infrastructure', detail: 'Windows, Linux, PowerShell' },
  { icon: ShieldCheck, label: 'Delivery', detail: 'APIs, automation, secure systems' },
];

const projects = [
  {
    number: '01', title: 'Warehouse Stock-Take Platform', category: 'Operations / Full-stack',
    summary: 'A browser-based counting workflow replacing a paper-and-Access process across main warehouse and overstock locations.',
    outcome: 'Direct data capture, automated count allocation, discrepancy handling and a live admin view.',
    stack: ['ASP.NET', 'C#', 'SQL Server'],
  },
  {
    number: '02', title: 'Reception & Evacuation System', category: 'Business systems / Integration',
    summary: 'A resilient reception kiosk and fire-register workflow designed for fast daily use and dependable emergency reporting.',
    outcome: 'Simple sign-in, central SQL records and automated SharePoint synchronisation.',
    stack: ['.NET', 'Debian', 'SQL', 'SharePoint'],
  },
  {
    number: '03', title: 'Commerce Automation Suite', category: 'E-commerce / Automation',
    summary: 'Connected services for Shopify promotions, gift cards, order flows and loyalty experiences across web and retail systems.',
    outcome: 'Less manual handling, consistent customer data and safer operational hand-offs.',
    stack: ['Shopify', 'GraphQL', 'PowerShell', 'SQL'],
  },
];

function Nav() {
  const [open, setOpen] = useState(false);
  const links = [['work', 'Work'], ['expertise', 'Expertise'], ['about', 'About'], ['contact', 'Contact']];
  useEffect(() => {
    const close = () => setOpen(false);
    window.addEventListener('resize', close);
    return () => window.removeEventListener('resize', close);
  }, []);
  return (
    <header className="site-nav">
      <a className="wordmark" href="#top" aria-label="Adrian Rudge home"><span className="prompt">&gt;</span> ar<span className="cursor" aria-hidden="true" /></a>
      <button className="menu-button" onClick={() => setOpen(!open)} aria-expanded={open} aria-label="Toggle navigation">{open ? <X /> : <Menu />}</button>
      <nav className={open ? 'nav-links open' : 'nav-links'} aria-label="Primary navigation">
        {links.map(([id, label]) => <a key={id} href={`#${id}`} onClick={() => setOpen(false)}>{label}</a>)}
        <a className="nav-cta" href={resumePdf} target="_blank" rel="noreferrer">Résumé <ArrowUpRight size={15} /></a>
      </nav>
    </header>
  );
}

function Hero() {
  return (
    <section className="hero" id="top">
      <div className="hero-grid" aria-hidden="true" />
      <div className="hero-copy reveal">
        <p className="eyebrow"><span className="status-dot" /> IT engineer · full-stack developer</p>
        <h1>I build systems<br />that make work<br /><em>work better.</em></h1>
        <p className="hero-intro">I turn operational problems into reliable software—connecting business data, internal tools and e-commerce systems from database to interface.</p>
        <div className="hero-actions">
          <a href="#work" className="button primary">View selected work <ArrowDownRight size={18} /></a>
          <a href="#contact" className="button text-button">Start a conversation <ChevronRight size={18} /></a>
        </div>
      </div>
      <div className="terminal-card reveal delay-1" aria-label="Professional profile summary">
        <div className="terminal-bar"><span /><span /><span /><small>adrian@portfolio:~</small></div>
        <div className="terminal-body">
          <p><span className="muted">$</span> whoami</p><p className="output">Adrian Rudge</p>
          <p><span className="muted">$</span> cat focus.txt</p><p className="output">Full-stack engineering<br />Business automation<br />Data & infrastructure</p>
          <p><span className="muted">$</span> uptime --career</p><p className="output success">always learning <span className="blink">_</span></p>
        </div>
        <div className="terminal-footer"><span>UK based</span><span>Available for opportunities</span></div>
      </div>
    </section>
  );
}

function Work() {
  return (
    <section className="section work" id="work">
      <div className="section-heading"><p className="kicker">01 / Selected work</p><h2>Systems built around<br />real operational needs.</h2></div>
      <div className="project-list">
        {projects.map(project => (
          <article className="project-card" key={project.number}>
            <div className="project-number">{project.number}</div>
            <div className="project-main"><p className="project-category">{project.category}</p><h3>{project.title}</h3><p>{project.summary}</p></div>
            <div className="project-result"><p><Check size={16} /> {project.outcome}</p><div className="tags">{project.stack.map(tag => <span key={tag}>{tag}</span>)}</div></div>
          </article>
        ))}
      </div>
    </section>
  );
}

function Expertise() {
  return (
    <section className="section expertise" id="expertise">
      <div className="section-heading compact"><p className="kicker">02 / Expertise</p><h2>From infrastructure<br />to interface.</h2><p>I work across the stack, which means fewer gaps between the problem, the data and the finished tool.</p></div>
      <div className="capability-grid">
        {capabilities.map(({ icon: Icon, label, detail }, index) => <div className="capability" key={label}><div className="capability-top"><Icon /><span>0{index + 1}</span></div><h3>{label}</h3><p>{detail}</p></div>)}
      </div>
      <div className="ticker" aria-label="Technology experience"><div>C# <span>•</span> .NET <span>•</span> SQL SERVER <span>•</span> POWERSHELL <span>•</span> REACT <span>•</span> NODE.JS <span>•</span> SHOPIFY <span>•</span> LINUX</div></div>
    </section>
  );
}

function About() {
  return (
    <section className="section about" id="about">
      <div className="about-label"><p className="kicker">03 / About</p></div>
      <div className="about-copy">
        <h2>Practical engineering,<br /><em>without the theatre.</em></h2>
        <p className="lead">I’m Adrian, an IT engineer who enjoys solving the awkward problems that sit between software, infrastructure and day-to-day operations.</p>
        <div className="about-columns"><p>My work starts with understanding how people actually use a system. From there, I design the data flow, build the interface and automate the hand-offs that usually create friction.</p><p>I’m most at home modernising legacy processes, connecting platforms and creating tools that are straightforward to operate, maintain and improve.</p></div>
        <div className="principles"><span><Zap size={16} /> Useful over flashy</span><span><Terminal size={16} /> Clear over clever</span><span><ShieldCheck size={16} /> Reliable by design</span></div>
      </div>
    </section>
  );
}

function Contact() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '', website: '' });
  const [captchaToken, setCaptchaToken] = useState(null);
  const [status, setStatus] = useState('');
  const submit = async event => {
    event.preventDefault();
    if (formData.website) return;
    if (!captchaToken) return setStatus('Please complete the CAPTCHA first.');
    setStatus('Sending…');
    try {
      const response = await fetch('https://adrianrudge.co.uk/api/contact', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...formData, captchaToken }) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Unable to send message.');
      setStatus(data.message || 'Message sent. Thank you.'); setFormData({ name: '', email: '', message: '', website: '' }); setCaptchaToken(null);
    } catch (error) { setStatus(error.message || 'Unable to send. Please try again.'); }
  };
  const change = event => setFormData({ ...formData, [event.target.name]: event.target.value });
  return (
    <section className="section contact" id="contact">
      <div className="contact-copy">
        <p className="kicker">04 / Contact</p><h2>Have a problem worth<br /><em>solving?</em></h2>
        <p>If you’re looking for someone who can understand the operation as well as the technology, let’s talk.</p>
        <div className="social-links"><a href="https://github.com/HexHawkeye" target="_blank" rel="noreferrer"><Github /> GitHub <ArrowUpRight /></a><a href="https://linkedin.com/in/adrian-rudge-2b3569321" target="_blank" rel="noreferrer"><Linkedin /> LinkedIn <ArrowUpRight /></a></div>
      </div>
      <form className="contact-form" onSubmit={submit}>
        <input name="website" className="honeypot" tabIndex="-1" autoComplete="off" value={formData.website} onChange={change} />
        <label>Name<input name="name" required value={formData.name} onChange={change} placeholder="Your name" /></label>
        <label>Email<input name="email" type="email" required value={formData.email} onChange={change} placeholder="you@company.com" /></label>
        <label>Message<textarea name="message" required rows="5" value={formData.message} onChange={change} placeholder="Tell me a little about the project…" /></label>
        <div className="captcha-wrap"><ReCAPTCHA sitekey="6Lf8o2ErAAAAADkmaReQrEzKqAZX6U0nEY5g1B9L" onChange={setCaptchaToken} theme="dark" /></div>
        <button className="button primary" type="submit">Send message <Mail size={17} /></button>
        {status && <p className="form-status" role="status">{status}</p>}
      </form>
    </section>
  );
}

function Home() {
  return <><SEO title="Adrian Rudge | IT Engineer & Full-Stack Developer" description="IT engineer and full-stack developer building reliable business systems, integrations and automation." /><Nav /><main><Hero /><Work /><Expertise /><About /><Contact /></main><footer><span>© {new Date().getFullYear()} Adrian Rudge</span><span className="footer-command">system.status: <b>online</b></span><a href="#top">Back to top ↑</a></footer></>;
}

export default function App() {
  return <BrowserRouter><Routes><Route path="/" element={<Home />} /><Route path="/admin-mode" element={<Admin exitAdmin={() => (window.location.href = '/')} />} /></Routes></BrowserRouter>;
}
